import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  FileSignature, Loader2, AlertCircle, Save, Trash2, Send, Ban, GitCompare, Pencil, CheckCircle2,
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import ModuleAccessDenied from '../../components/ui/ModuleAccessDenied';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import contractService from '../../services/contracts';
import { formatDateTimeInTz, useOrgTimezone } from '../../utils/timezone';
import { ContractDiffView, ContractHistory, Fingerprint } from '../../components/cultivar/contracts/ContractBits';
import {
  CONTRACT_TIER_LABELS, DERIVED_MERGE_FIELDS, contractTokens,
  type ContractDiff, type ContractFamilyDetail, type ContractPublishResult, type ContractVersion,
} from '../../types/contracts';

const errText = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);

/**
 * One contract: the version in force, the draft of the next one, every
 * version there has been, and everything that happened to it.
 *
 * Drafting needs a cultivar pbr or admin seat. Publishing and withdrawing need
 * the cultivar admin seat; the server enforces the same split.
 */
export default function ContractDetail() {
  const { id } = useParams<{ id: string }>();
  const familyId = Number(id);
  const orgTz = useOrgTimezone();
  const { hasAccess, loading: licLoading } = useModuleLicense('cultivar', ['viewer', 'pbr', 'admin']);
  const { hasAccess: canWrite } = useModuleLicense('cultivar', ['pbr', 'admin']);
  const { hasAccess: isAdmin } = useModuleLicense('cultivar', 'admin');

  const [family, setFamily] = useState<ContractFamilyDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [historyKey, setHistoryKey] = useState(0);

  // Draft editing
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState('');
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const textRef = useRef<HTMLTextAreaElement | null>(null);

  // Publish
  const [publishing, setPublishing] = useState(false);
  const [summary, setSummary] = useState('');
  const [publishBusy, setPublishBusy] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [publishResult, setPublishResult] = useState<ContractPublishResult | null>(null);
  const [draftDiff, setDraftDiff] = useState<ContractDiff | null>(null);

  // Withdraw
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState('');
  const [withdrawError, setWithdrawError] = useState<string | null>(null);

  // Details
  const [editingDetails, setEditingDetails] = useState(false);
  const [detailName, setDetailName] = useState('');
  const [detailDesc, setDetailDesc] = useState('');
  const [detailError, setDetailError] = useState<string | null>(null);

  // Compare
  const [compareFrom, setCompareFrom] = useState<number | 0>(0);
  const [compareTo, setCompareTo] = useState<number | 0>(0);
  const [compare, setCompare] = useState<ContractDiff | null>(null);
  const [compareError, setCompareError] = useState<string | null>(null);
  const [openVersion, setOpenVersion] = useState<number | null>(null);

  const applyFamily = useCallback((f: ContractFamilyDetail) => {
    setFamily(f);
    setHistoryKey(k => k + 1);
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await contractService.get(familyId);
      if (res.success) applyFamily(res.data);
      else setLoadError(res.error.message);
    } catch (e) {
      setLoadError(errText(e, 'Could not load the contract'));
    }
  }, [familyId, applyFamily]);

  useEffect(() => { if (hasAccess) load(); }, [hasAccess, load]);

  // What changes against the version in force, shown before publishing.
  useEffect(() => {
    setDraftDiff(null);
    if (!family?.draft || !family.current) return;
    contractService.diff(familyId, family.current.id, family.draft.id)
      .then(res => { if (res.success) setDraftDiff(res.data); })
      .catch(() => { /* the draft text is still shown in full */ });
  }, [familyId, family?.draft?.id, family?.draft?.updated_at, family?.current?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- keyed on the ids and edit time, not the whole object

  const tokens = useMemo(() => contractTokens(body), [body]);
  const derivedKeys = useMemo(() => new Set(DERIVED_MERGE_FIELDS.map(f => f.key)), []);

  const startEditing = () => {
    setBody(family?.draft?.body_richtext ?? family?.current?.body_richtext ?? '');
    setDraftError(null);
    setEditing(true);
  };

  const insertToken = (key: string) => {
    const el = textRef.current;
    const token = `{{${key}}}`;
    if (!el) { setBody(b => b + token); return; }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    const next = body.slice(0, start) + token + body.slice(end);
    setBody(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const saveDraft = async () => {
    setDraftError(null);
    if (!body.trim()) { setDraftError('The contract needs some text'); return; }
    setSavingDraft(true);
    try {
      const res = await contractService.saveDraft(familyId, body);
      if (res.success) { applyFamily(res.data); setEditing(false); }
      else setDraftError(res.error.message);
    } catch (e) {
      setDraftError(errText(e, 'Could not save the draft'));
    } finally {
      setSavingDraft(false);
    }
  };

  const discardDraft = async () => {
    if (!window.confirm('Discard this draft? The published version is not affected.')) return;
    try {
      const res = await contractService.discardDraft(familyId);
      if (res.success) { applyFamily(res.data); setEditing(false); }
      else setDraftError(res.error.message);
    } catch (e) {
      setDraftError(errText(e, 'Could not discard the draft'));
    }
  };

  const publish = async () => {
    setPublishError(null);
    if (!summary.trim()) { setPublishError('Say what changed in this version — it is kept with it for good'); return; }
    if (!family?.draft) return;
    setPublishBusy(true);
    try {
      const res = await contractService.publish(familyId, summary.trim(), family.draft.text_hash);
      if (res.success) {
        setPublishResult(res.data);
        applyFamily(res.data.family);
        setPublishing(false);
        setSummary('');
      } else {
        setPublishError(res.error.message);
      }
    } catch (e) {
      setPublishError(errText(e, 'Could not publish'));
    } finally {
      setPublishBusy(false);
    }
  };

  const withdraw = async () => {
    setWithdrawError(null);
    if (!withdrawReason.trim()) { setWithdrawError('Say why this contract is being withdrawn'); return; }
    try {
      const res = await contractService.withdraw(familyId, withdrawReason.trim());
      if (res.success) { applyFamily(res.data); setWithdrawing(false); setWithdrawReason(''); }
      else setWithdrawError(res.error.message);
    } catch (e) {
      setWithdrawError(errText(e, 'Could not withdraw the contract'));
    }
  };

  const saveDetails = async () => {
    setDetailError(null);
    try {
      const res = await contractService.updateDetails(familyId, { name: detailName.trim(), description: detailDesc.trim() || null });
      if (res.success) { applyFamily(res.data); setEditingDetails(false); }
      else setDetailError(res.error.message);
    } catch (e) {
      setDetailError(errText(e, 'Could not save'));
    }
  };

  const runCompare = async () => {
    setCompare(null);
    setCompareError(null);
    if (!compareFrom || !compareTo || compareFrom === compareTo) { setCompareError('Pick two different versions'); return; }
    try {
      const res = await contractService.diff(familyId, compareFrom, compareTo);
      if (res.success) setCompare(res.data);
      else setCompareError(res.error.message);
    } catch (e) {
      setCompareError(errText(e, 'Could not compare'));
    }
  };

  if (licLoading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary-600" /></div>;
  }
  if (!hasAccess) return <ModuleAccessDenied moduleName="Cultivar" />;

  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader title="Contract" icon={<FileSignature className="h-6 w-6 text-white" />} />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-sm">{loadError}</div>
        </div>
      </div>
    );
  }
  if (!family) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader title="Contract" icon={<FileSignature className="h-6 w-6 text-white" />} />
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary-600" /></div>
      </div>
    );
  }

  const current = family.current;
  const draft = family.draft;
  const published = family.versions.filter(v => v.status !== 'draft');
  const pendingReissuable = family.pending_agreements.filter(p => p.can_reissue).length;
  const pendingManual = family.pending_agreements.length - pendingReissuable;

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title={family.name}
        subtitle={CONTRACT_TIER_LABELS[family.license_tier] + (family.cultivar_id ? ` · ${family.cultivar_trade_name || family.cultivar_name} only` : '')}
        icon={<FileSignature className="h-6 w-6 text-white" />}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">

        {publishResult && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm text-emerald-900 space-y-1">
            <p className="font-semibold flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" /> v{publishResult.family.current?.version} is now the version sent
            </p>
            <p>
              {publishResult.reissue.reissued.length} unsigned agreement(s) re-sent on the new version
              {publishResult.redispatch.redispatched.length > 0 && `; ${publishResult.redispatch.redispatched.length} order(s) sent the new contract`}.
            </p>
            {publishResult.reissue.withdrawn.length > 0 && (
              <p className="text-amber-800">
                Withdrawn and needing to be sent again by hand:{' '}
                {publishResult.reissue.withdrawn.map(w => (
                  <Link key={w.id} to={`/cultivar/licences/${w.id}/agreement`} className="underline mr-2">
                    #{w.id} {w.licensee_org_name}
                  </Link>
                ))}
              </p>
            )}
            {publishResult.redispatch.failed.length > 0 && (
              <p className="text-red-700">
                Not re-sent: {publishResult.redispatch.failed.map(f => `order #${f.order_id} (${f.reason})`).join('; ')}
              </p>
            )}
          </div>
        )}

        {/* ---------------------------------------------------------------- in force */}
        <Card>
          <CardHeader><CardTitle>Version in force</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {current ? (
              <>
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="text-lg font-semibold text-neutral-900">v{current.version}</span>
                  <span className="text-sm text-neutral-600">
                    Published {current.published_at ? formatDateTimeInTz(current.published_at, orgTz) : ''}
                    {current.published_by_name ? ` by ${current.published_by_name}` : ''}
                  </span>
                </div>
                {current.change_summary && <p className="text-sm text-neutral-700">{current.change_summary}</p>}
                <Fingerprint hash={current.content_hash} />
                <p className="text-xs text-neutral-500">
                  {current.agreements_sent} agreement(s) and {current.order_documents_sent} order contract(s) sent on this version.
                </p>
                <details className="text-sm">
                  <summary className="cursor-pointer text-primary-600">Read the text</summary>
                  <pre className="mt-2 whitespace-pre-wrap break-words font-sans text-sm text-neutral-800 bg-neutral-50 border border-neutral-200 rounded p-3 max-h-[32rem] overflow-y-auto">
                    {current.body_richtext}
                  </pre>
                </details>
              </>
            ) : (
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
                Nothing is published, so this contract cannot be sent.
                {isAdmin ? ' Publish the draft below when it is ready.' : ' A cultivar admin publishes it.'}
              </p>
            )}

            {current && isAdmin && !withdrawing && (
              <Button variant="outline" size="sm" onClick={() => setWithdrawing(true)}>
                <Ban className="h-4 w-4" /> Stop sending this contract
              </Button>
            )}
            {withdrawing && (
              <div className="border border-red-200 bg-red-50 rounded-lg p-3 space-y-2">
                <p className="text-sm text-red-900">
                  v{current?.version} will be retired with nothing in its place.
                  {family.pending_agreements.length > 0 && ` ${family.pending_agreements.length} unsigned agreement(s) will be withdrawn and the recipients told.`}
                  {' '}Signed agreements are not affected.
                </p>
                <label htmlFor="withdraw-reason" className="block text-xs font-semibold text-neutral-700">Why *</label>
                <textarea
                  id="withdraw-reason"
                  rows={2}
                  value={withdrawReason}
                  onChange={e => setWithdrawReason(e.target.value)}
                  className="w-full border border-neutral-300 rounded px-3 py-2 text-sm"
                />
                {withdrawError && <p className="text-sm text-red-700">{withdrawError}</p>}
                <div className="flex flex-wrap gap-2 justify-end">
                  <Button variant="outline" size="sm" onClick={() => setWithdrawing(false)}>Cancel</Button>
                  <Button variant="danger" size="sm" onClick={withdraw}>Withdraw contract</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ---------------------------------------------------------------- draft */}
        <Card>
          <CardHeader><CardTitle>{draft ? `Draft v${draft.version}` : 'Next version'}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {!editing && !draft && (
              <>
                <p className="text-sm text-neutral-600">
                  No draft. {current ? `Changes are made in a new version; v${current.version} stays in force until it is published.` : ''}
                </p>
                {canWrite && (
                  <Button variant="outline" size="sm" onClick={startEditing}>
                    <Pencil className="h-4 w-4" /> {current ? `Start v${(family.versions[0]?.version ?? 0) + 1}` : 'Write the first version'}
                  </Button>
                )}
              </>
            )}

            {!editing && draft && (
              <>
                <p className="text-xs text-neutral-500">
                  Last saved {formatDateTimeInTz(draft.updated_at, orgTz)}
                  {draft.created_by_name ? ` · started by ${draft.created_by_name}` : ''}. Not sent to anybody.
                </p>
                {draftDiff ? (
                  <>
                    <p className="text-sm text-neutral-700">
                      Against v{current?.version}: {draftDiff.stats.added} line(s) added, {draftDiff.stats.removed} removed.
                    </p>
                    <ContractDiffView lines={draftDiff.lines} />
                  </>
                ) : (
                  <pre className="whitespace-pre-wrap break-words font-sans text-sm text-neutral-800 bg-neutral-50 border border-neutral-200 rounded p-3 max-h-[24rem] overflow-y-auto">
                    {draft.body_richtext}
                  </pre>
                )}
                <div className="flex flex-wrap gap-2">
                  {canWrite && (
                    <Button variant="outline" size="sm" onClick={startEditing}>
                      <Pencil className="h-4 w-4" /> Edit draft
                    </Button>
                  )}
                  {canWrite && (
                    <Button variant="ghost" size="sm" onClick={discardDraft}>
                      <Trash2 className="h-4 w-4" /> Discard
                    </Button>
                  )}
                  {isAdmin && !publishing && (
                    <Button variant="primary" size="sm" onClick={() => { setPublishing(true); setPublishError(null); }}>
                      <Send className="h-4 w-4" /> Publish v{draft.version}
                    </Button>
                  )}
                </div>
                {!isAdmin && (
                  <p className="text-xs text-neutral-500">A cultivar admin publishes drafts. They are emailed whenever a draft changes.</p>
                )}
                {draftError && <p className="text-sm text-red-700">{draftError}</p>}

                {publishing && (
                  <div className="border border-primary-200 bg-primary-50 rounded-lg p-3 space-y-2">
                    <p className="text-sm text-neutral-800 font-semibold">Publishing v{draft.version} will:</p>
                    <ul className="text-sm text-neutral-700 list-disc pl-5 space-y-0.5">
                      <li>fix its text for good — it can never be edited, only replaced by a later version;</li>
                      {current && <li>retire v{current.version}; nothing can be sent on it again;</li>}
                      {pendingReissuable > 0 && <li>withdraw {pendingReissuable} unsigned agreement(s) and re-send them on v{draft.version};</li>}
                      {pendingManual > 0 && <li>withdraw {pendingManual} unsigned agreement(s) issued before versioning, which you will need to send again by hand;</li>}
                      {family.used_by_programs.length > 0 && <li>cancel any programme order contracts still out for signature on an older version and send this one;</li>}
                      <li>leave signed agreements on the version they were signed on.</li>
                    </ul>
                    <label htmlFor="publish-summary" className="block text-xs font-semibold text-neutral-700">What changed in this version *</label>
                    <textarea
                      id="publish-summary"
                      rows={3}
                      value={summary}
                      onChange={e => setSummary(e.target.value)}
                      placeholder="e.g. Clause 7 royalty reporting moved to quarterly"
                      className="w-full border border-neutral-300 rounded px-3 py-2 text-sm"
                    />
                    {publishError && (
                      <div className="text-sm text-red-700 flex gap-2"><AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" /> {publishError}</div>
                    )}
                    <div className="flex flex-wrap gap-2 justify-end">
                      <Button variant="outline" size="sm" onClick={() => setPublishing(false)}>Cancel</Button>
                      <Button variant="primary" size="sm" onClick={publish} disabled={publishBusy}>
                        {publishBusy ? 'Publishing…' : `Publish v${draft.version}`}
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}

            {editing && (
              <>
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Filled in automatically when sent (click to insert at the cursor):</p>
                  <div className="flex flex-wrap gap-1">
                    {DERIVED_MERGE_FIELDS.map(f => (
                      <button
                        key={f.key}
                        type="button"
                        onClick={() => insertToken(f.key)}
                        title={f.label}
                        className="px-2 py-0.5 bg-neutral-100 hover:bg-primary-100 rounded text-xs font-mono"
                      >
                        {`{{${f.key}}}`}
                      </button>
                    ))}
                  </div>
                </div>
                <label htmlFor="contract-body" className="sr-only">Contract text</label>
                <textarea
                  id="contract-body"
                  ref={textRef}
                  rows={18}
                  value={body}
                  onChange={e => setBody(e.target.value)}
                  className="w-full border border-neutral-300 rounded px-3 py-2 font-mono text-sm"
                />
                {tokens.some(t => !derivedKeys.has(t)) && (
                  <p className="text-xs text-neutral-600">
                    Typed by the sender each time:{' '}
                    {tokens.filter(t => !derivedKeys.has(t)).map(t => <code key={t} className="mr-1">{`{{${t}}}`}</code>)}
                    — left empty, they print as a line to fill by hand.
                  </p>
                )}
                {draftError && <p className="text-sm text-red-700">{draftError}</p>}
                <div className="flex flex-wrap gap-2 justify-end">
                  <Button variant="outline" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
                  <Button variant="primary" size="sm" onClick={saveDraft} disabled={savingDraft}>
                    <Save className="h-4 w-4" /> {savingDraft ? 'Saving…' : 'Save draft'}
                  </Button>
                </div>
                <p className="text-xs text-neutral-500">Saving a draft sends nothing. The cultivar admins are emailed what changed.</p>
              </>
            )}
          </CardContent>
        </Card>

        {/* ---------------------------------------------------------------- where used */}
        <Card>
          <CardHeader><CardTitle>Where it is used</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            {family.used_by_groups.map(g => (
              <p key={`g${g.id}`}>
                Sublicence group <Link to="/cultivar/sublicence-groups" className="text-primary-600 hover:underline">{g.code} — {g.name}</Link>
                {' '}({[g.as_master && 'master', g.as_schedule && 'per-order schedule'].filter(Boolean).join(', ')}{g.status !== 'active' ? ', closed' : ''})
              </p>
            ))}
            {family.used_by_programs.map(p => (
              <p key={`p${p.program_id}${p.role}`}>
                Programme <Link to={`/cultivar/admin/programs/${p.program_id}/contract-templates`} className="text-primary-600 hover:underline">{p.program_name}</Link>
                {' '}— {p.role.replace(/_/g, ' ')}{p.required ? ' (required)' : ''}
              </p>
            ))}
            {family.used_by_groups.length === 0 && family.used_by_programs.length === 0 && (
              <p className="text-neutral-500">
                {family.license_tier === 'nursery_master_sublicence'
                  ? 'No sublicence group uses this as its master yet. Set it on the Sublicence Groups page.'
                  : 'Chosen when an agreement is issued; no programme role or group names it.'}
              </p>
            )}
            {family.pending_agreements.length > 0 && (
              <div className="pt-2">
                <p className="font-medium text-neutral-800">Awaiting signature</p>
                <ul className="mt-1 space-y-0.5">
                  {family.pending_agreements.map(p => (
                    <li key={p.id}>
                      <Link to={`/cultivar/licences/${p.id}/agreement`} className="text-primary-600 hover:underline">#{p.id}</Link>
                      {' '}{p.licensee_org_name} · {p.template_version ? `v${p.template_version}` : 'version not recorded'}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ---------------------------------------------------------------- versions */}
        <Card>
          <CardHeader><CardTitle>All versions</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <ul className="divide-y divide-neutral-100">
              {family.versions.map((v: ContractVersion) => (
                <li key={v.id} className="py-2 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <span className="font-medium">v{v.version}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      v.status === 'published' ? 'bg-emerald-100 text-emerald-800'
                        : v.status === 'draft' ? 'bg-amber-100 text-amber-800'
                          : 'bg-neutral-100 text-neutral-600'}`}>
                      {v.status === 'published' ? 'In force' : v.status === 'draft' ? 'Draft' : 'Retired'}
                    </span>
                    {v.published_at && v.status !== 'draft' && (
                      <span className="text-xs text-neutral-500">published {formatDateTimeInTz(v.published_at, orgTz)}{v.published_by_name ? ` by ${v.published_by_name}` : ''}</span>
                    )}
                  </div>
                  {v.change_summary && <p className="text-sm text-neutral-700 break-words">{v.change_summary}</p>}
                  {v.content_hash && <Fingerprint hash={v.content_hash} />}
                  <button
                    type="button"
                    onClick={() => setOpenVersion(o => (o === v.id ? null : v.id))}
                    className="block text-xs text-primary-600 hover:underline"
                  >
                    {openVersion === v.id ? 'Hide text' : 'Read this version'}
                  </button>
                  {openVersion === v.id && (
                    <pre className="mt-1 whitespace-pre-wrap break-words font-sans text-sm text-neutral-800 bg-neutral-50 border border-neutral-200 rounded p-3 max-h-[24rem] overflow-y-auto">
                      {v.body_richtext}
                    </pre>
                  )}
                </li>
              ))}
            </ul>

            {family.versions.length > 1 && (
              <div className="border-t border-neutral-100 pt-3 space-y-2">
                <p className="text-sm font-medium text-neutral-800 flex items-center gap-2"><GitCompare className="h-4 w-4" /> Compare two versions</p>
                <div className="flex flex-wrap items-center gap-2">
                  <label htmlFor="cmp-from" className="sr-only">From version</label>
                  <select id="cmp-from" value={compareFrom} onChange={e => setCompareFrom(Number(e.target.value))}
                    className="border border-neutral-300 rounded px-2 py-1 text-sm w-full sm:w-auto">
                    <option value={0}>From…</option>
                    {family.versions.map(v => <option key={v.id} value={v.id}>v{v.version} ({v.status})</option>)}
                  </select>
                  <label htmlFor="cmp-to" className="sr-only">To version</label>
                  <select id="cmp-to" value={compareTo} onChange={e => setCompareTo(Number(e.target.value))}
                    className="border border-neutral-300 rounded px-2 py-1 text-sm w-full sm:w-auto">
                    <option value={0}>To…</option>
                    {family.versions.map(v => <option key={v.id} value={v.id}>v{v.version} ({v.status})</option>)}
                  </select>
                  <Button variant="outline" size="sm" onClick={runCompare}>Compare</Button>
                </div>
                {compareError && <p className="text-sm text-red-700">{compareError}</p>}
                {compare && (
                  <>
                    <p className="text-sm text-neutral-700">
                      v{compare.from.version} → v{compare.to.version}: {compare.stats.added} added, {compare.stats.removed} removed
                    </p>
                    <ContractDiffView lines={compare.lines} />
                  </>
                )}
              </div>
            )}
            {published.length === 0 && <p className="text-sm text-neutral-500">Nothing published yet.</p>}
          </CardContent>
        </Card>

        {/* ---------------------------------------------------------------- details */}
        <Card>
          <CardHeader><CardTitle>Details</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            {!editingDetails ? (
              <>
                <p className="text-neutral-700 break-words">{family.description || <span className="text-neutral-400">No description</span>}</p>
                {canWrite && (
                  <Button variant="ghost" size="sm" onClick={() => { setDetailName(family.name); setDetailDesc(family.description || ''); setEditingDetails(true); }}>
                    <Pencil className="h-4 w-4" /> Rename or describe
                  </Button>
                )}
              </>
            ) : (
              <div className="space-y-2">
                <label htmlFor="detail-name" className="block text-xs font-semibold text-neutral-700">Name</label>
                <input id="detail-name" value={detailName} onChange={e => setDetailName(e.target.value)}
                  className="w-full border border-neutral-300 rounded px-3 py-2 text-sm" />
                <label htmlFor="detail-desc" className="block text-xs font-semibold text-neutral-700">Description</label>
                <textarea id="detail-desc" rows={2} value={detailDesc} onChange={e => setDetailDesc(e.target.value)}
                  className="w-full border border-neutral-300 rounded px-3 py-2 text-sm" />
                <p className="text-xs text-neutral-500">Versions already published keep the name they were published under.</p>
                {detailError && <p className="text-sm text-red-700">{detailError}</p>}
                <div className="flex flex-wrap gap-2 justify-end">
                  <Button variant="outline" size="sm" onClick={() => setEditingDetails(false)}>Cancel</Button>
                  <Button variant="primary" size="sm" onClick={saveDetails}>Save</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <ContractHistory kind="family" id={familyId} refreshKey={historyKey} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
